// `reports/enrichment-report/*`. Three counters over one API call.
import { createElement, type ReactElement } from 'react'
import type { EnrichmentReportResponse } from '@woo-ext/api'
import { InfoTooltip } from './InfoTooltip.tsx'
import { formatCount } from './format.ts'

export interface EnrichmentReportProps {
  report?: EnrichmentReportResponse
  loading: boolean
}

const ENRICHMENT_TOOLTIP =
  'Candidates with enriched or additional information regarding their skill set, location, ' +
  'work experience, contact details, and/or education'

/**
 * ⚠️ THE HOST ELEMENT CARRIES THE COLUMN LAYOUT. `enrichment-report { display: flex;
 * flex-direction: column; flex: 1 }` is written against the component ELEMENT, and the inner
 * `.enrichment-report` is a different rule set that `@extend`s `%report`. Both exist and a
 * tree with only the div loses the flex context the page lays the sections out in.
 */
export function EnrichmentReport({ report, loading }: EnrichmentReportProps): ReactElement {
  return createElement(
    'enrichment-report',
    null,
    <div className="enrichment-report">
      <div className="report-section-header">
        <h2>
          <span>Enrichment</span>{' '}
          <InfoTooltip title="Enrichment" content={ENRICHMENT_TOOLTIP} placement="top-left" />
        </h2>
      </div>
      <div className="report-section-body">
        {/* Upstream's `ng-if` is `reportLoading && !report`, so a RELOAD keeps the old
            numbers on screen rather than flashing the spinner over them. */}
        {loading && report === undefined ? (
          <div className="woo-loader-wrapper">
            <i className="far fa-spinner fa-spin woo-loader" />
          </div>
        ) : null}
        {!loading && report !== undefined ? (
          <div className="enrichment-stats">
            <Block
              className="enrichment-stats-all-time-block"
              icon="fa-calendar-alt"
              label="All time"
              value={report.totalCandidateEnriched}
            />
            {/* The two period blocks use `fa-calendar-day`, not `fa-calendar-alt`. */}
            <Block
              className="enrichment-stats-last-90-block"
              icon="fa-calendar-day"
              label="Last 3 Months"
              value={report.totalCandidateEnrichedLast90Days}
            />
            {/* ⚠️ `…Last180Days`. Upstream's INTERFACE declares `…Last18Days`, which nothing
                reads; the template and the server both say 180, and so does the label. */}
            <Block
              className="enrichment-stats-last-180-block"
              icon="fa-calendar-day"
              label="Last 6 Months"
              value={report.totalCandidateEnrichedLast180Days}
            />
          </div>
        ) : null}
      </div>
    </div>,
  )
}

function Block({
  className,
  icon,
  label,
  value,
}: {
  className: string
  icon: string
  label: string
  value: number
}): ReactElement {
  return (
    <div className={`${className} enrichment-stats-block`}>
      <div className="vis-section">
        <i className={`vis fas ${icon}`} />
      </div>
      <div className="data-section">
        <div className="data-label">{label}</div>
        <div className="data-value">
          {/* ⚠️ THE SPACE IS EXPLICIT BECAUSE JSX WOULD DROP IT. Upstream has the two spans on
              separate lines of HTML, where the newline and indentation collapse to one space;
              JSX strips a whitespace-only line between elements entirely. Without `{' '}` the
              counter reads "0candidates" — a 1,055-pixel diff at 700px, and the only thing
              wrong with this screen on its first comparison. */}
          <span className="value">{formatCount(value)}</span>{' '}
          <span>candidates</span>
        </div>
      </div>
    </div>
  )
}
